import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function JavaExceptionHandling() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const exceptionModules = useMemo(() => [
        {
            title: "1. The Try-Catch Mechanism",
            code: `public class Main {\n  public static void main(String[] args) {\n    try {\n      int result = 10 / 0; \n    } catch (ArithmeticException e) {\n      System.out.println("Handled: Division by zero is not allowed.");\n    }\n  }\n}`,
            desc: "The 'try' block contains code that might throw an exception. If an error occurs, execution jumps immediately to the 'catch' block, preventing the program from crashing.",
            diagram: ""
        },
        {
            title: "2. Multiple Catch Blocks",
            code: `try {\n  int[] arr = new int[5];\n  arr[10] = 50;\n} catch (ArithmeticException e) {\n  System.out.println("Math error");\n} catch (ArrayIndexOutOfBoundsException e) {\n  System.out.println("Memory Index error");\n} catch (Exception e) {\n  System.out.println("General error");\n}`,
            desc: "Java allows multiple catch blocks to handle different types of errors uniquely. Critical Rule: Always place specific exceptions before general ones (like 'Exception') to avoid masking specific errors.",
            diagram: ""
        },
        {
            title: "3. The Finally Block",
            code: `try {\n  // Attempt sensitive operation (e.g., Database call)\n} catch (Exception e) {\n  System.out.println("Error handled: " + e.getMessage());\n} finally {\n  System.out.println("Cleanup: Closing all open resources...");\n}`,
            desc: "The 'finally' block is a safety net. It executes regardless of whether an exception was thrown or caught. It is primarily used for resource cleanup like closing files or socket connections.",
            diagram: ""
        },
        {
            title: "4. Throwing Manual Exceptions",
            code: `static void validateAge(int age) {\n  if (age < 18) {\n    throw new ArithmeticException("Underage: Access Denied");\n  }\n}\n\npublic static void main(String[] args) {\n  validateAge(15);\n}`,
            desc: "The 'throw' keyword is used to manually trigger an exception based on custom logic. This is essential for enforcing specific business rules within your application flow.",
            diagram: ""
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    JAVA EXCEPTION HANDLING
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Tutorial Introduction & Coverage */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Tutorial Overview
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                            Welcome to the deep dive into <strong>Error Management</strong>. In this tutorial, we are covering the fundamental and advanced strategies to make your Java applications "bulletproof." By the end of this module, you will master:
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            {[
                                "Basic Try-Catch Logic",
                                "Handling Multiple Error Scenarios",
                                "Resource Cleanup with Finally",
                                "Custom Error Propagation (Throwing)",
                                "Checked vs Unchecked Exceptions"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckIcon sx={{ color: "goldenrod", fontSize: 20 }} />
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Typography variant="h6" fontWeight={800} color="goldenrod" mb={1}>
                            Graceful Error Recovery
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9 }}>
                            An <strong>Exception</strong> is an object that describes an error condition.
                            If not handled, it triggers a "stack trace" and terminates your program.
                            Proper handling ensures a smooth user experience.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Key Technical Concepts</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.8, pl: 1, lineHeight: 1.8 }}>
                            • <strong>Checked Exceptions:</strong> Must be handled or declared (e.g., IOException).<br />
                            • <strong>Unchecked Exceptions:</strong> Subclasses of RuntimeException (e.g., NullPointerException).<br />
                            • <strong>Try-with-resources:</strong> A modern syntax to ensure resources close automatically.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* ✅ Detailed Learning Modules */}
                {exceptionModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <Box sx={{ textAlign: "center", my: 2 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontStyle: "italic", mb: 1, display: "block" }}>
                                    Conceptual Logic Flow:
                                </Typography>
                                {item.diagram}
                            </Box>

                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/javadashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Back to Top Floating Action Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
